import qs from 'qs';
import Geo from 'models/Geo';
import { isDirectTicketingEnabled } from 'store/ticketingPublic';
import { accessNested, asList } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
import dataProviderToTicketProvider from 'utils/dataProviderToTicketProvider';

// Finds the first non-undefined and non-null value (equivalent to v1 || v2 || v3 ... except returns the value if it's not null/undefined, even if falsy like 0, '' or false)
function customDefault(...args) {
	return args.find(v => v != undefined);
}

function replaceURLParameters(url, studio, movie, region) {
	// getCopyWithParams from utils/clientUtils
	// return getCopyWithParams({ $MOVIE: movie, $STUDIO: studio, $REGION: region }, url);
	return url.replace(/=(?:\$|%24)(STUDIO|MOVIE|REGION)(&|$)/g, (match, type, end) => {
		switch (type) {
			case 'STUDIO':
				return '=' + ( studio ) + end;
			case 'MOVIE':
				return '=' + ( movie ) + end;
			case 'REGION':
				return '=' + ( region ) + end;
			default:
				return match;
		}
	});
}

// Makes sure mainTheater has fandango and movietickets ids from alternateTheater if it doesn't have its own
function checkTicketProviderIds(mainTheater, alternateTheater) {
	if (dataProviderToTicketProvider[alternateTheater.providerId]) {
		return attachTicketProvider(mainTheater, alternateTheater);
	}
	if (!mainTheater.tms_id) mainTheater.tms_id = alternateTheater.tms_id;
	if (!mainTheater.fandango) mainTheater.fandango = alternateTheater.fandango;
	if (!mainTheater.movietickets) mainTheater.movietickets = alternateTheater.movietickets;
	if (!mainTheater.atom) mainTheater.atom = alternateTheater.atom;
	mainTheater.movies.forEach(m => {
		if (m.fandango && m.movietickets && m.atom) return;
		mainTheater.screenings.find(screening => {
			if (screening.movie.id !== m.id || !screening.datetime) return;
			let matching = alternateTheater.screenings.filter(sc2 => +sc2.datetime === +screening.datetime);
			if (matching.length !== 1) return;
			if (!m.fandango) m.fandango = matching[0].movie.fandango;
			if (!m.movietickets) m.movietickets = matching[0].movie.movietickets;
			if (!m.atom) m.atom = matching[0].movie.atom;
			return true;
		});
	});
}
function attachTicketProvider(theater, alternateTheater) {
	alternateTheater.screenings.forEach(screening => {
		if (!screening.srcTicketProviders) {
			return;
		}
		let matching = theater.screenings.find(s2 => s2.date === screening.date && s2.time24 === screening.time24);
		if (matching) {
			matching.srcTicketProviders = Object.assign({}, matching.srcTicketProviders, screening.srcTicketProviders);
		}
	});
}

export default class Theater {
	// TODO: pass in currentPage to avoid hardcoded "pages.showtimes.data"
	constructor(id, attr, { screenings, exhibitor, clientGeo, appData, theaterMasters, theaterMasterDoc, firstBatch, pinned, activePage }) {
		let copy = appData.copy;
		let providers = accessNested(activePage, 'data.providers') || accessNested(appData, 'pages.showtimes.data.providers') || {};
		let regionProviders = accessNested(appData, 'pages.showtimes.data.providerLocation');
		this.id = id;
		this.pinned = attr.pinned !== undefined ? attr.pinned : !!pinned;
		this.active = false;
		this.name = attr.name;
		let addr = attr.address;
		let city = attr.city;
		if (appData.options.useEnglishCopy) {
			this.name = attr.name_en || attr.name;
			addr =  attr.address_en || attr.address;
			city =  attr.city_en || attr.city;
		}
		this.screenings = screenings;
		let addedMoviesCache = {};
		this.movies = screenings.reduce((a, s) => {
			if (!s.movie || addedMoviesCache[s.movie.id]) return a;
			addedMoviesCache[s.movie.id] = true;
			a.push(s.movie);
			return a;
		}, []);

		this.activeScreenings = screenings;
		// if (filters) {
		// 	this.activeScreenings = this.activeScreenings.filter((s) => {
		// 		let passes = inactiveScreeningsFilter(filters.format, s.movie.format);
		// 		return passes;
		// 	});
		// }
		this.filteredScreenings = screenings;
		this.filterPass = this.filteredScreenings.length ? true : false;
		this.exhibitor = exhibitor;
		this.logo = (exhibitor && exhibitor.logo) ? exhibitor.logo : 'default.png';
		this.activeExhibitor = true;
		this.activeScreening = null;
		let addressParts = [addr, city];
		// For webedia US, also add the state to the displayed address
		if (attr.country === 'us' && (attr.provider_id === 'webus' || attr.provider_id === 'wwm' || attr.provider_id === 'mx') && attr.state) {
			addressParts.push(attr.state);
		}
		let fullAddress = addressParts.filter(e => e).join(', ');
		this.location = {
			'country' : attr.country,
			'fullAddress': fullAddress,
			'address': addr,
			'city': city,
			'state': attr.state
		};
		let website = attr.url;
		if (website) {
			// Apparently sometimes the importers are giving us multiple websites separated by , or space
			// (think this should rather be fixed in the backend)
			website = website.split(',')[0].split(' ')[0];
			if (website.lastIndexOf('http://', 0) !== 0 && website.lastIndexOf('https://', 0) !== 0)
				website = 'http://' + website;
			this.website = website;
		}
		// if(!this.website && exhibitor && exhibitor.domain) {
		// 	this.website = 'http://' + exhibitor.domain;
		// }
		const distanceUnit = copy['$' + (attr.country || '').toUpperCase() + '_DISTANCE_UNIT'] || copy.$DISTANCE_UNIT;

		this.masterId = attr.theater_master_id;
		this.masterDoc = theaterMasterDoc || {};
		this.providerId = attr.provider_id;
		this.providerTheaterId = attr.provider_theater_id;
		this.pid = this.providerId + ':' + this.providerTheaterId;
		this.providerExhibitorId = attr.provider_exhibitor_id;
		this.geo = new Geo({ lat: attr.lat, lon: attr.lon, countryCode: attr.country, city: city });
		this.distance = this.geo.distanceFrom(clientGeo, distanceUnit);
		this.distanceUnit = distanceUnit;
		this.enabled = this.screenings.length > 0;

		let disabledList = asList(accessNested(appData, 'pages.showtimes.data.disabledTheaters', []));
		if (disabledList.indexOf(this.id) !== -1 || disabledList.indexOf(this.pid) !== -1) {
			this.enabled = false;
		}

		if (attr.wwm_ticketing) {
			this.movietickets = attr.movietickets_theater_id;
			this.fandango = attr.fandango_theater_id;
			this.tms_id = attr.tms_id;
		}
		this.atom = attr.atom_theater_id;
		this.firstBatch = firstBatch;

		let getRank = t => +customDefault(accessNested(regionProviders, [t.location.country, t.providerId, 'rank']), (providers[t.providerId] || {}).rank, 100);

		if (theaterMasters && this.masterId && this.enabled) {
			let currMaster = theaterMasters[this.masterId];
			if (currMaster && providers) {
				let currRank = getRank(this);
				let masterRank = getRank(currMaster);
				if (this.hasBadDeeplinks()) {
					currRank = Infinity;
				}
				if (currMaster.hasBadDeeplinks()) {
					masterRank = Infinity;
				}
				if (currRank < masterRank) {
					checkTicketProviderIds(this, currMaster);
					currMaster.enabled = false;
					theaterMasters[this.masterId] = this;
				} else {
					checkTicketProviderIds(currMaster, this);
					// After potentially getting new fandango ids, recalculate the urls
					currMaster.computeUrls(appData, activePage);
					this.enabled = false;
				}
			} else theaterMasters[this.masterId] = this;
		}

		// TODO: currentPage
		let exhibitorDomain = accessNested(this, 'exhibitor.domain', '');
		let mfixList = asList(accessNested(appData, 'pages.showtimes.data.midnightFix', {}));
		let midnightFix = mfixList.indexOf(exhibitorDomain) !== -1;
		this.screenings.forEach(screening => {
			if (midnightFix && screening.nextDayDatetime) {
				screening.offsetDate(-1, 0, 0, appData.copy);
			}
		});
		this.computeUrls(appData, activePage);
	}

	// Returns whether this theater has bad quality deeplinks
	// For instance, MX without instant ticketing may have theater homepage links only
	// Those theater should have the lowest priority in the ranking
	hasBadDeeplinks() {
		if (this.providerId === 'mx' && !isDirectTicketingEnabled(this)) {
			let mxLinks = accessNested(this.masterDoc, 'mxLinks');
			if (typeof mxLinks === 'undefined') {
				mxLinks = accessNested(this.masterDoc, 'mx_links', false);
			}
			return mxLinks !== true;
		}
		return false;
	}

	// Computes the screenings urls of a theater (fandango etc.)
	computeUrls(appData, activePage) {
		let exhibitorDomain = accessNested(this, 'exhibitor.domain', '');

		// TODO: currentPage
		let deeplinksObj = accessNested(appData, 'pages.showtimes.data.overrideDeeplinks', {});
		let overrideDeeplinks = deeplinksObj[exhibitorDomain] || deeplinksObj['*'] || deeplinksObj;
		let isValidDLContext = ctx => (ctx.url || ctx.url === false) || ctx.movietickets || typeof ctx.fandango !== 'undefined';
		let powRefQueryString = accessNested(appData, 'pages.showtimes.data.powRefQueryString', 'referral=powster');

		if (typeof overrideDeeplinks === 'string') {
			overrideDeeplinks = [{ url: overrideDeeplinks }];
		} else if (isValidDLContext(overrideDeeplinks)) {
			overrideDeeplinks = [overrideDeeplinks];
		} else if (!Array.isArray(overrideDeeplinks)) {
			overrideDeeplinks = Object.keys(overrideDeeplinks).map(k => {
				// If it's a domain obj, do not list it
				if (k.match(/^\w{2,}\.\w{2,}$/)) return;
				return overrideDeeplinks[k];
			});
		}
		overrideDeeplinks = overrideDeeplinks.filter(e => e).sort((a, b) => {
			if (a.order == b.order) return 0;
			if (a.order == undefined) return 1;
			if (b.order == undefined) return -1;
			return a.order - b.order;
		});

		// Checks an overrideDeeplink context object
		let checkDLContext = (ctx, s) => {
			if (!isValidDLContext(ctx)) {
				return false;
			}
			// Check that every recognized key in the context object is validated for this screening
			return Object.keys(ctx).every(key => {
				let val = ctx[key];
				if (!val) return true;
				switch (key) {
					case 'exhibitor':
					case 'domain':
						if (typeof val !== 'string') {
							return true;
						}
						return val.split(',').map(e => e.trim()).indexOf(exhibitorDomain) !== -1;
					case 'movie':
						let movie = s.movie;
						return val === movie.id || val === movie.provider_id + ':' + movie.provider_movie_id;
					case 'format':
						return val === s.movie.format.slug;
					case 'time':
						if (typeof val !== 'string') {
							return true;
						}
						return val.split(',').map(e => e.trim()).indexOf(s.time24) !== -1;
					case 'hour':
						if (val.length > 2) {
							return +val.slice(0, 2) === s.hour && +val.slice(2) === s.minute;
						}
						return +val === s.hour;
					case 'minute':
						return +val === s.minute;
					case 'day':
					case 'date':
						let dateFormat = 'ddMM';
						if (val.match(/^\d{2}-\d{2}$/)) {
							dateFormat = 'MM-dd';
						} else if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
							dateFormat = 'yyyy-MM-dd';
						}
						return s.stDate && val === formatDateTime(s.stDate.date, dateFormat);
					case 'theater':
						if (typeof val !== 'string') {
							return true;
						}
						const potentialMatches = [this.id, this.pid];
						return val.split(',').find(val => potentialMatches.indexOf(val.trim()) !== -1);
					case 'nameContains':
						return this.name.toLowerCase().indexOf(val.toLowerCase()) !== -1;
				}
				// Not a valid condition, ignore
				return true;
			});
		};

		let affiliate = appData.meta.affiliate || 'pow';
		let tProviders = this.providers = [];
		let ticketProviders = accessNested(appData, 'pages.showtimes.data.ticketProviders', {});

		if (activePage
			&& activePage.data
			&& activePage.data.ticketProviders
			&& typeof activePage.data.ticketProviders === 'object'
			&& activePage.data.ticketProviders.override
		) {
			ticketProviders = activePage.data.ticketProviders;
		}


		// Retro-compatibility for apps that relied on the global showtimes page value (TODO: remove that when not needed anymore)
		let useCinemaUrls = customDefault(accessNested(activePage, 'data.useCinemaUrls'), accessNested(appData, 'pages.showtimes.data.useCinemaUrls'), true);
		if (dataProviderToTicketProvider[this.providerId]) {
			useCinemaUrls = false;
		}

		const affiliateCodeExhibitors = [
			'amccinemas.co.uk',
			'amctheatres.com',
			'amccinemas.com.hk'
		];
		const movieSlug = accessNested(appData, 'meta.title.slug', '');
		const studioSlug = accessNested(appData, 'meta.studio.slug', '');
		const region = accessNested(appData, 'meta.region.slug', '');

		let addAffiliateCode = url => {
			// (Vue only at the moment - add the movie name to the affiliate code)
			url = replaceURLParameters(url, studioSlug, movieSlug, region);
			return url.replace(/([?&])SC_CMP=AFF_POW(&|$)/, '$1SC_CMP=AFF_POW_' + movieSlug + '$2');
		};

		if (affiliateCodeExhibitors.indexOf(exhibitorDomain) !== -1) {
			addAffiliateCode = url => {
				let parts = url.split('#');
				url = parts.shift();
				let hash = parts.length ? '#' + parts.join('#') : '';
				let qsPos = url.indexOf('?');

				let parsedQS = {};
				if (qsPos !== -1) {
					parsedQS = qs.parse(url.slice(qsPos + 1), { strictNullHandling: true });
					url = url.slice(0, qsPos);
				}
				parsedQS.affiliateCode = 'pow';

				url += '?' + qs.stringify(parsedQS, { strictNullHandling: true }) + hash;
				return replaceURLParameters(url, studioSlug, movieSlug, region);
			};
		}

		this.screenings.forEach(screening => {
			let matchingDeeplink = overrideDeeplinks.find(dl => checkDLContext(dl, screening)) || overrideDeeplinks;
			if (!screening.preventDeeplinkOverride && matchingDeeplink && (matchingDeeplink.url || matchingDeeplink.url === false)) {
				screening.url = matchingDeeplink.url;
			}

			let urls = screening.urls = {};
			let screeningUrl = screening.url;
			// If we the screeningsUrl is not set to false, but we want to use cinema url.
			if (!screeningUrl && screeningUrl !== false && useCinemaUrls && this.website) {
				screeningUrl = this.website;
			}
			if (screeningUrl && !!ticketProviders.cinema && !urlContainsBlacklistTheater(screeningUrl, this) && allowedTicketProvider('cinema', screening)) {
				if (tProviders.indexOf('cinema') === -1) tProviders.push('cinema');
				urls.cinema = addAffiliateCode(screeningUrl);
			}

			if (screening.srcTicketProviders) {
				Object.keys(screening.srcTicketProviders).forEach(ticketProvider => {
					if (!allowedTicketProvider(ticketProvider, screening)) {
						return;
					}
					urls[ticketProvider] = screening.srcTicketProviders[ticketProvider];
					if (tProviders.indexOf(ticketProvider) === -1) tProviders.push(ticketProvider);
				});
			}

			let movie = screening.movie;

			if (allowedTicketProvider('fandango', screening)) {
				if (matchingDeeplink && (typeof matchingDeeplink.fandango === 'string' || matchingDeeplink.fandango === false)) {
					if (matchingDeeplink.fandango) {
						if (tProviders.indexOf('fandango') === -1) tProviders.push('fandango');
						urls.fandango = matchingDeeplink.fandango;
					}
				} else {
					const movieFandango = (screening.doc && screening.doc.movie_code_id) || movie.fandango;
					if (screening.fandangoUrl) {
						if (tProviders.indexOf('fandango') === -1) tProviders.push('fandango');
						urls.fandango = screening.fandangoUrl;
					} else if (this.fandango && !!ticketProviders.fandango && movieFandango && screening.datetime && this.tms_id) {
						if (tProviders.indexOf('fandango') === -1) tProviders.push('fandango');
						const formattedDate = formatDateTime(screening.datetime, 'MM\\-dd\\-yyyy\\+HH\\:mm');
						urls.fandango = 'http://www.fandango.com/redirect.aspx?&mid=' + movieFandango + '&a=&tid=' + this.tms_id +
						'&dte=0&date=' + formattedDate + '&' + powRefQueryString;
					}
				}
			}

			if (this.movietickets && !!ticketProviders.movietickets && movie.movietickets && screening.datetime && allowedTicketProvider('movietickets', screening)) {
				if (tProviders.indexOf('movietickets') === -1) tProviders.push('movietickets');
				if (matchingDeeplink && matchingDeeplink.movietickets) {
					urls.movietickets = matchingDeeplink.movietickets;
				} else if (this.location.country === 'us') {
					const movieId = screening.movieticketsMovieId || movie.movietickets;
					const dateTime = formatDateTime(screening.datetime, 'yyyy\\-MM\\-dd\\THH\\:mm');
					urls.movietickets = `https://www.movietickets.com/switchboard/checkout?tid=${this.movietickets}&mid=${movieId}&datetime=${dateTime}&a=${affiliate}&${powRefQueryString}`;
				}
			}

			if (this.atom && !!ticketProviders.atom && movie.atom && screening.datetime && screening.tz_offset && allowedTicketProvider('atom', screening)) {
				if (tProviders.indexOf('atom') === -1) tProviders.push('atom');
				// urls.atom = `atom:///ViewVenueDetails?venueId=${this.atom}`;
				let formattedDate;
				if (screening.nextDayDatetime) {
					formattedDate = formatDateTime(screening.nextDayDatetime, 'yyyy\\-MM\\-dd\\THH\\:mm\\:ss');
				} else {
					formattedDate = formatDateTime(screening.datetime, 'yyyy\\-MM\\-dd\\THH\\:mm\\:ss');
				}
				if (Object.prototype.hasOwnProperty.call(movie, 'customAtomURL')) {
					urls.atom = movie.customAtomURL;
				} else {
					urls.atom = `https://www.atomtickets.com/checkout/redirect?venueId=${this.atom}&productionId=${movie.atom}&showtime=${formattedDate}${screening.tz_offset}`;
				}
			}
		});
		// let ticketProviders = appData.pages.showtimes.data.ticketProviders;
		tProviders.sort((a, b) => ((ticketProviders[a] && ticketProviders[a].order) || 0) - ((ticketProviders[b] && ticketProviders[b].order) || 0));
	}
}

// Update the screenings
function validateFilter(filter, obj, tester) {
	if (!filter) return true;
	if (!obj) return filter.allowNulls || !filter.current || !filter.current.length;
	let mp = filter.matchProp || 'id';
	if (typeof tester !== 'function') {
		let isArray = Array.isArray(obj);
		tester = v => isArray ? obj.indexOf(v[mp]) !== -1 : obj[mp] === v[mp];
	}
	if (filter.excluded && filter.excluded.find(v => tester(v, obj, filter)))
		return false;
	return !filter.current || !filter.current.length || filter.current.find(v => tester(v, obj, filter));
}

function inactiveScreeningsFilter(filter, obj) {
	if (!obj || !filter || !filter.disabled || !filter.disabled.length) return true;
	let mp = filter.matchProp || 'id';
	let val = obj[mp];

	return !filter.disabled.find(v => v[mp] == val);
}

function validateMovie(val, obj) {
	if (!val) return false;
	if (obj.id === val.id || obj.provider_id + ':' + obj.provider_movie_id === val.id) return true;
	return val.slug && val.slug === obj.slug;
}

function validateFormat(theater) {
	return (val, obj, filter) => {
		let mp = filter.matchProp || 'id';
		if (val.reald && !accessNested(theater, 'masterDoc.reald')) return false;
		return obj[mp] === val[mp];
	};
}

// Tester function for theater filter - check id and provider id
function validateTheater(val, obj) {
	if (!val || !val.id) return false;
	val = val.id;
	if (obj.id === val) return true;
	let vals = val.split(':');
	let prov = vals.shift();
	let pid = vals.join(':');
	if (!prov || !pid) return false;
	return obj.providerId === prov && (pid === '*' || obj.providerTheaterId === pid);
}

export function filterTheater(theater, filters) {

	theater.activeScreenings = theater.screenings.filter((s) => {
		let passes = inactiveScreeningsFilter(filters.format, s.movie.format);
		return passes;
	});

	theater.filteredScreenings = theater.activeScreenings.filter((s) => {
		let passes = validateFilter(filters.timeOfDay, s.timeOfDay) &&
			validateFilter(filters.movie, s.movie, validateMovie) &&
			validateFilter(filters.format, s.movie.format, validateFormat(theater)) &&
			validateFilter(filters.stDate, s.nextDaySTDate || s.stDate) &&
			validateFilter(filters.hour, { id: s.hour }) &&
			validateFilter(filters.minute, { id: s.minute }) &&
			// validateFilter(filters.exhibitor, theater.exhibitor) &&
			validateFilter(filters.ticketProvider, theater.providers);
		return passes;
	}).sort((a, b) => a.datetime < b.datetime ? -1 : 1);

	let filterPass = theater.filteredScreenings.length ? true : false;

	// remove active if doesn't pass filter (no filtered screenings)
	theater.active = theater.active && !filterPass ? false : theater.active;

	// Is it active
	theater.activeExhibitor =
		validateFilter(filters.exhibitor, theater.exhibitor) &&
		validateFilter(filters.theater, theater, validateTheater);
	let pid = theater.providerId;
	if (pid === 'wwm' || pid === 'webuk' || pid === 'webus') {
		theater.activeExhibitor = theater.activeExhibitor && validateFilter(filters.webedia, theater);
	}

	// Edit the instance but also return the properties to update the store object properly

	// in case we need to sort by date instead of distance
	// let firstDatetime = theater.firstDatetime = theater.filteredScreenings.length && theater.filteredScreenings[0].datetime;
	// let firstDate = theater.firstDate = accessNested(theater, 'filteredScreenings.0.stDate.date');
	let firstDate = theater.firstDate = theater.filteredScreenings.reduce((p, s) => {
		let date = accessNested(s, 'stDate.date');
		if (!p) return +date;
		return date ? Math.min(p, date) : p;
	}, undefined);

	return {
		filteredScreenings: theater.filteredScreenings,
		activeExhibitor: theater.activeExhibitor,
		// firstDatetime,
		firstDate,
		filterPass,
		active: theater.active
	};
}

/**
 * Test if URL contains blacklisted theater.
 *
 * @param {string} url The url to test for.
 * @returns {boolean} True is the url contains the theater hostname.
 */
function urlContainsBlacklistTheater(url, theater) {
	if (theater.providerId === 'pow') {
		return false;
	}
	let matcher = /((\w*\.)?(arclightcinemas\.com|pacifictheatres\.com))/i;
	return matcher.test(url);
}

function allowedTicketProvider(provider, screening) {
	if (screening.disabledTicketsProviders && screening.disabledTicketsProviders.indexOf(provider) !== -1) {
		return false;
	}
	return true;
}
